import { memo } from 'react'
import { Clock, Flame } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  AvatarGroup,
  PriorityChip,
  SourceGlyph,
  TypeGlyph,
} from '@/components/common/badges'
import { CrossContextFlags } from '@/components/common/cross-context-flags'
import { IssueContextMenu } from '@/components/common/issue-context-menu'
import { daysAtStage, isHot } from '@/lib/computed'

import { cn } from '@/lib/utils'
import type { WorkIssue } from '@/types/github'

export type IssueCardProps = {
  issue: WorkIssue
  /** Stable callback — the handler closure is built inside the memoized card. */
  onOpen?: (issue: WorkIssue) => void
  /** Extra badge content per view (e.g. Effort on Backlog). */
  showEffort?: boolean
  /** The card's own context (e.g. `backlog`), so its flag doesn't echo back. */
  excludeContext?: string
  /**
   * False inside a sortable wrapper: the wrapper is the (single) focusable
   * button-role element there, so the card itself must stay non-interactive
   * to avoid nested buttons (a11y).
   */
  interactive?: boolean
  className?: string
}

function CardInner({ issue, onOpen, showEffort, excludeContext, interactive = true, className }: IssueCardProps) {
  const days = daysAtStage(null, issue.createdAt)
  const hot = isHot(days, issue.stage)
  const shortSprint = issue.sprint?.replace('Sprint ', '')

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={() => onOpen?.(issue)}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen?.(issue)
              }
            }
          : undefined
      }
      className={cn(
        'bg-card cursor-pointer rounded-lg px-2.5 py-2 text-left transition-shadow',
        'shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-card-hover)]',
        className,
      )}
    >
      <div className="text-muted-foreground flex items-center gap-1.5">
        <span className="text-faint-foreground font-mono text-[11px]">#{issue.number}</span>
        <TypeGlyph typeName={issue.typeName} />
        <span className="flex-1" />
        <CrossContextFlags issueId={issue.id} excludeContext={excludeContext} />
        <SourceGlyph source={issue.source} />
      </div>
      <p
        title={issue.title}
        className="mt-1 mb-1.5 line-clamp-2 text-[12.8px] leading-[1.35] font-medium"
      >
        {issue.title}
      </p>
      <div className="mb-2 flex flex-wrap items-center gap-1">
        <PriorityChip priority={issue.priority} />
        {shortSprint && (
          <Badge variant="outline" className="px-1.5 py-px font-mono text-[10.5px] font-normal">
            {shortSprint}
          </Badge>
        )}
        {issue.client && (
          <Badge variant="outline" className="px-1.5 py-px font-mono text-[10.5px] font-normal">
            {issue.client}
          </Badge>
        )}
        {issue.product && (
          <Badge variant="secondary" className="px-1.5 py-px text-[10.5px] font-normal">
            {issue.product}
          </Badge>
        )}
        {showEffort && issue.effort && (
          <Badge variant="outline" className="px-1.5 py-px text-[10.5px] font-normal">
            {issue.effort}
          </Badge>
        )}
      </div>
      <div className="flex items-center justify-between gap-1.5">
        <AvatarGroup users={issue.assignees} />
        <span
          className={cn(
            'text-faint-foreground inline-flex items-center gap-1 text-[11px]',
            hot && 'font-medium text-[var(--p1)]',
          )}
        >
          {hot ? <Flame className="size-3" /> : <Clock className="size-3" />}
          {days}d old
        </span>
      </div>
    </div>
  )
}

export const IssueCard = memo(function IssueCard(props: IssueCardProps) {
  return (
    <IssueContextMenu issue={props.issue}>
      <div>
        <CardInner {...props} />
      </div>
    </IssueContextMenu>
  )
})
